/**
 * DFE Materials API Contract
 *
 * Returns the available materials catalogue that can be picked when building a
 * new DFE submission. Sourced from `bt_rates_info` on the backend.
 *
 * Endpoint: GET /jobs/dfe-materials
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

export const dfeMaterialSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  unit: z.string().nullable(),
  price: z.string().nullable(),
  synthetic_code: z.string().nullable(),
});

export const dfeMaterialsResponseSchema = z.object({
  data: z.array(dfeMaterialSchema),
});

export type DfeMaterial = z.infer<typeof dfeMaterialSchema>;
export type DfeMaterialsResponse = z.infer<typeof dfeMaterialsResponseSchema>;

/**
 * DFE Materials API Contract
 *
 * Base path: /jobs/dfe-materials  (mounted in apps/backend/src/domains/works-lite/jobs/routes.ts).
 * Deliberately on a sibling path to `/jobs/dfe` so it cannot collide with `/jobs/dfe/:jobId`.
 */
export const dfeMaterialsContract = c.router({
  getDfeMaterials: {
    method: 'GET',
    path: '/',
    responses: {
      200: dfeMaterialsResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get the materials catalogue for DFE line items',
    description: 'Returns active materials sourced from bt_rates_info.',
  },
});
